import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input/input';
import { doc, getDoc, setDoc, getFirestore, collection, getDocs } from 'firebase/firestore';
import ClubRadiusMap from './ClubRadiusMap';
import { useAuth } from './contexts/AuthContext';

const ABSOLUTE_MIN_PTI = -30.0;
const ABSOLUTE_MAX_PTI = 100.0;

function ProfilePage() { // Removed unused prop
  // Internal state with defaults
  const [playerName, setPlayerName] = useState('');
  const [myPti, setMyPti] = useState(40.0);
  const [minPti, setMinPti] = useState(ABSOLUTE_MIN_PTI);
  const [maxPti, setMaxPti] = useState(ABSOLUTE_MAX_PTI);
  const [isWillingToTravel, setIsWillingToTravel] = useState(false);
  const [preferredClubId, setPreferredClubId] = useState('');
  const [defaultTravelRadius, setDefaultTravelRadius] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Restore: fetch all clubs on mount
  const [allKnownClubs, setAllKnownClubs] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const db = getFirestore();
        const profileRef = doc(db, 'userProfiles', user.uid);
        const snap = await getDoc(profileRef);
        if (snap.exists()) {
          const data = snap.data();
          setPlayerName(data.name || user.displayName || user.email || '');
          setMyPti(data.myPti ?? 40.0);
          setMinPti(data.minPti ?? ABSOLUTE_MIN_PTI);
          setMaxPti(data.maxPti ?? ABSOLUTE_MAX_PTI);
          setIsWillingToTravel(data.isWillingToTravel ?? false);
          setPreferredClubId(data.preferredClubId || '');
          setDefaultTravelRadius(data.defaultTravelRadius ?? 10);
          setStagedPreferredEmail(data.preferredEmail || user.email || '');
          setStagedCellPhone(data.cellPhone || '');
          setStagedAllowTexts(data.allowTexts ?? false);
        }
      } catch (e) {
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user]);

  // Restore: fetch all clubs on mount
  useEffect(() => {
    const fetchClubs = async () => {
      const db = getFirestore();
      const clubsRef = collection(db, 'locations');
      const snapshot = await getDocs(clubsRef);
      // Sort clubs alphabetically by ClubName, then by state if available
      const clubs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const nameA = (a.ClubName || a.name || '').toLowerCase();
          const nameB = (b.ClubName || b.name || '').toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          // If names are equal, sort by state
          const stateA = (a.State || '').toLowerCase();
          const stateB = (b.State || '').toLowerCase();
          if (stateA < stateB) return -1;
          if (stateA > stateB) return 1;
          return 0;
        });
      setAllKnownClubs(clubs);
    };
    fetchClubs();
  }, []);

  // Local state for staged changes
  const [stagedPlayerName, setStagedPlayerName] = useState('');
  const [stagedMyPti, setStagedMyPti] = useState(40.0);
  const [stagedMinPti, setStagedMinPti] = useState(ABSOLUTE_MIN_PTI);
  const [stagedMaxPti, setStagedMaxPti] = useState(ABSOLUTE_MAX_PTI);
  const [stagedIsWillingToTravel, setStagedIsWillingToTravel] = useState(false);
  const [stagedPreferredClubId, setStagedPreferredClubId] = useState('');
  const [stagedDefaultTravelRadius, setStagedDefaultTravelRadius] = useState(10);
  const [stagedPreferredEmail, setStagedPreferredEmail] = useState('');
  const [stagedCellPhone, setStagedCellPhone] = useState('');
  const [stagedAllowTexts, setStagedAllowTexts] = useState(false);
  const [isEditingClub, setIsEditingClub] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // When profile loads, update staged state
  useEffect(() => {
    setStagedPlayerName(playerName);
    setStagedMyPti(myPti);
    // If the user hasn't set a custom PTI range, center it on their PTI.
    if (minPti === ABSOLUTE_MIN_PTI && maxPti === ABSOLUTE_MAX_PTI) {
      const initialRange = 1.0;
      setStagedMinPti(Math.max(ABSOLUTE_MIN_PTI, myPti - initialRange));
      setStagedMaxPti(Math.min(ABSOLUTE_MAX_PTI, myPti + initialRange));
    } else {
      setStagedMinPti(minPti);
      setStagedMaxPti(maxPti);
    }
    setStagedIsWillingToTravel(isWillingToTravel);
    setStagedPreferredClubId(preferredClubId);
    if (defaultTravelRadius !== stagedDefaultTravelRadius) {
      // Don't overwrite the staged value if the user is editing it
    } else {
      setStagedDefaultTravelRadius(defaultTravelRadius);
    }
    setStagedPreferredEmail(user?.email || '');
    setHasUnsavedChanges(false); // Reset unsaved changes when profile loads
  }, [playerName, myPti, minPti, maxPti, isWillingToTravel, preferredClubId, defaultTravelRadius, stagedDefaultTravelRadius, user?.email]);

  // Track unsaved changes, but don't show on initial load
  const [hasInteracted, setHasInteracted] = useState(false);
  useEffect(() => {
    if (
      stagedPlayerName !== playerName ||
      stagedMyPti !== myPti ||
      stagedMinPti !== minPti ||
      stagedMaxPti !== maxPti ||
      stagedIsWillingToTravel !== isWillingToTravel ||
      stagedPreferredClubId !== preferredClubId ||
      stagedDefaultTravelRadius !== defaultTravelRadius ||
      stagedPreferredEmail !== (user?.email || '') ||
      stagedCellPhone !== '' ||
      stagedAllowTexts !== false
    ) {
      if (hasInteracted) setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [stagedPlayerName, stagedMyPti, stagedMinPti, stagedMaxPti, stagedIsWillingToTravel, stagedPreferredClubId, stagedDefaultTravelRadius, stagedPreferredEmail, stagedCellPhone, stagedAllowTexts, playerName, myPti, minPti, maxPti, isWillingToTravel, preferredClubId, defaultTravelRadius, user?.email, hasInteracted]);

  // Mark as interacted on any field change
  const handleFieldChange = (setter) => (value) => {
    setHasInteracted(true);
    setter(value);
  };

  // Save staged profile to Firestore
  const handleSaveProfile = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const db = getFirestore();
      const profileRef = doc(db, 'userProfiles', user.uid);
      await setDoc(profileRef, {
        name: stagedPlayerName,
        myPti: stagedMyPti,
        minPti: stagedMinPti,
        maxPti: stagedMaxPti,
        isWillingToTravel: stagedIsWillingToTravel,
        preferredClubId: stagedPreferredClubId,
        defaultTravelRadius: parseInt(stagedDefaultTravelRadius, 10) || 0,
        preferredEmail: stagedPreferredEmail,
        cellPhone: stagedCellPhone,
        allowTexts: stagedAllowTexts,
        lastProfileUpdate: new Date().toISOString()
      }, { merge: true });

      alert("Profile saved successfully!");
      setHasUnsavedChanges(false); // Reset unsaved changes after save
    } catch (e) {
      setError('Failed to save profile: ' + (e.message || e.toString()));
    } finally {
      setLoading(false);
    }
  };

  // Custom filter for react-select: partial, case-insensitive match (same as LocationSearchPage logic)
  const filterClubOption = (option, inputValue) => {
    if (!option.label || !inputValue) return true;
    // Make matches prominent by wrapping the match in <strong> (for menu rendering)
    return option.label.toLowerCase().includes(inputValue.trim().toLowerCase());
  };

  // Custom formatOptionLabel to highlight matches
  const formatOptionLabel = (option, { inputValue }) => {
    if (!inputValue || !option.label) return option.label;
    const lower = option.label.toLowerCase();
    const idx = lower.indexOf(inputValue.trim().toLowerCase());
    if (idx === -1) return option.label;
    const before = option.label.slice(0, idx);
    const match = option.label.slice(idx, idx + inputValue.length);
    const after = option.label.slice(idx + inputValue.length);
    return (
      <span>
        {before}
        <strong style={{ background: '#ffe066', borderRadius: 2 }}>{match}</strong>
        {after}
      </span>
    );
  };

  const clubToDisplay = allKnownClubs.find(c => c.id === stagedPreferredClubId);
  const clubLabel = clubToDisplay
    ? (clubToDisplay.ClubState ? `${clubToDisplay.ClubName || clubToDisplay.name} (${clubToDisplay.ClubState})` : (clubToDisplay.ClubName || clubToDisplay.name))
    : 'None selected';

  const customSelectStyles = {
    singleValue: (provided) => ({
      ...provided,
      color: '#9ca3af', // Tailwind's gray-400
    }),
  };

  // Warn user if they try to leave with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return (
    <div className="bg-sky-50 py-8 px-10 rounded-lg shadow-xl w-full max-w-2xl mt-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        My Profile
      </h2>
      {error && (
        <p className="text-red-500 text-center mb-4 text-sm font-medium">{error}</p>
      )}
      {hasUnsavedChanges && hasInteracted && (
        <div className="text-yellow-700 bg-yellow-100 border-l-4 border-yellow-400 p-2 mb-4 text-center rounded" role="alert">
          Unsaved changes. Don’t forget to save!
        </div>
      )}
      <div className="flex items-center mb-4">
        <label htmlFor="playerName" className="w-32 text-gray-700 text-sm font-semibold">
          Your Name:
        </label>
        <input
          type="text"
          id="playerName"
          className="flex-grow shadow appearance-none border rounded-lg py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Enter your name"
          value={stagedPlayerName}
          onChange={e => handleFieldChange(setStagedPlayerName)(e.target.value)}
          disabled={loading}
        />
      </div>
      <div className="flex items-center mb-4">
        <label htmlFor="myPti" className="w-32 text-gray-700 text-sm font-semibold">
          Your PTI:
        </label>
        <input
          type="number"
          id="myPti"
          min={ABSOLUTE_MIN_PTI}
          max={ABSOLUTE_MAX_PTI}
          step="1"
          className="shadow appearance-none border rounded-lg w-24 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={stagedMyPti}
          onChange={e => handleFieldChange(setStagedMyPti)(parseFloat(e.target.value))}
          disabled={loading}
        />
      </div>
      <div className="flex items-center mb-4">
        <label htmlFor="preferredEmail" className="w-32 text-gray-700 text-sm font-semibold">
          Preferred Email:
        </label>
        <input
          type="email"
          id="preferredEmail"
          className="flex-grow shadow appearance-none border rounded-lg py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={stagedPreferredEmail}
          onChange={e => handleFieldChange(setStagedPreferredEmail)(e.target.value)}
          disabled={loading}
        />
      </div>
      <div className="flex items-center mb-6">
        <label htmlFor="cellPhone" className="w-32 text-gray-700 text-sm font-semibold">
          Cell Phone:
        </label>
        <div className="flex-grow flex items-center gap-4">
          <PhoneInput
            id="cellPhone"
            className="flex-grow shadow appearance-none border rounded-lg py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter your cell phone number"
            value={stagedCellPhone}
            onChange={handleFieldChange(setStagedCellPhone)}
            country="US"
            disabled={loading}
          />
          <label className="inline-flex items-center whitespace-nowrap">
            <input
              type="checkbox"
              className="form-checkbox text-purple-600 h-4 w-4"
              checked={stagedAllowTexts}
              onChange={e => handleFieldChange(setStagedAllowTexts)(e.target.checked)}
              disabled={loading}
            />
            <span className="ml-2 text-gray-700 text-sm font-semibold">I don't mind receiving automated texts</span>
          </label>
        </div>
      </div>
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-semibold mb-2">
          Preferred Opponent PTI Range: <span className="font-bold text-sky-600">{stagedMinPti.toFixed(1)} - {stagedMaxPti.toFixed(1)}</span>
        </label>
        <div className="w-full pt-4 px-4 md:px-8">
          <div className="">
            <Slider
              range
              min={ABSOLUTE_MIN_PTI}
              max={ABSOLUTE_MAX_PTI}
              step={0.5}
              value={[stagedMinPti, stagedMaxPti]}
              onChange={([min, max]) => {
                setHasInteracted(true);
                setStagedMinPti(min);
                setStagedMaxPti(max);
              }}
              disabled={loading}
              allowCross={false}
              trackStyle={[{ backgroundColor: '#0ea5e9', height: 8 }]}
              handleStyle={[
                { borderColor: '#0284c7', borderWidth: 2, backgroundColor: 'white', width: 24, height: 24, marginTop: -8 },
                { borderColor: '#0284c7', borderWidth: 2, backgroundColor: 'white', width: 24, height: 24, marginTop: -8 },
              ]}
              railStyle={{ backgroundColor: '#bae6fd', height: 8 }}
              marks={{
                [ABSOLUTE_MIN_PTI]: <span className="font-bold text-gray-600">{ABSOLUTE_MIN_PTI}</span>,
                0: '0',
                25: '25',
                50: '50',
                [ABSOLUTE_MAX_PTI]: <span className="font-bold text-gray-600">{ABSOLUTE_MAX_PTI}</span>
              }}
              dotStyle={{
                borderColor: '#9ca3af',
                backgroundColor: '#e5e7eb',
                width: 8,
                height: 8,
                bottom: -2
              }}
              activeDotStyle={{
                borderColor: '#0ea5e9',
                backgroundColor: '#0ea5e9'
              }}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-8 text-center">
          Adjust the slider to set your preferred opponent PTI range.
          <br />
          Your selection is not visible to others.
        </p>
      </div>
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-semibold mb-2">
          Home Club:
        </label>
        {isEditingClub ? (
          <Select
            id="preferredClub"
            options={allKnownClubs.map(club => ({
              value: club.id,
              label: club.State ? `${club.ClubName || club.name} (${club.ClubState})` : (club.ClubName || club.name)
            }))}
            value={allKnownClubs.map(club => ({
              value: club.id,
              label: club.ClubState ? `${club.ClubName || club.name} (${club.ClubState})` : (club.ClubName || club.name)
            })).find(option => option.value === stagedPreferredClubId) || null}
            onChange={option => {
              setHasInteracted(true);
              setStagedPreferredClubId(option ? option.value : '');
              setIsEditingClub(false);
            }}
            onBlur={() => setIsEditingClub(false)}
            isDisabled={loading}
            isClearable
            filterOption={filterClubOption}
            formatOptionLabel={formatOptionLabel}
            placeholder="Type or select a club..."
            className="react-select-container mb-1"
            classNamePrefix="react-select"
            styles={customSelectStyles}
            autoFocus
          />
        ) : (
          <div className="flex items-center justify-between p-3 border rounded-lg bg-white shadow-sm">
            <span className="text-gray-800">{clubLabel}</span>
            <button
              onClick={() => setIsEditingClub(true)}
              className="text-sm font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50"
              disabled={loading}
            >
              Change
            </button>
          </div>
        )}
      </div>
      <div className="mb-6">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            className="form-checkbox text-purple-600 h-4 w-4"
            checked={stagedIsWillingToTravel}
            onChange={e => handleFieldChange(setStagedIsWillingToTravel)(e.target.checked)}
            disabled={loading}
          />
          <span className="ml-2 text-gray-700 font-semibold">I'm willing to travel</span>
        </label>
      </div>
      {stagedIsWillingToTravel && (
        <div className="mb-6 flex flex-col md:flex-row md:items-start md:gap-6">
          <div className="flex-1">
            <label htmlFor="defaultTravelRadius" className="block text-gray-700 text-sm font-semibold mb-2">
              Default Travel Radius (miles):
            </label>
            <input
              type="number"
              id="defaultTravelRadius"
              className="shadow appearance-none border rounded-lg w-24 py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={stagedDefaultTravelRadius}
              onChange={e => handleFieldChange(setStagedDefaultTravelRadius)(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="flex-1 mt-4 md:mt-0">
            <ClubRadiusMap
              homeClub={allKnownClubs.find(c => c.id === stagedPreferredClubId) || {}}
              radiusMiles={Number(stagedDefaultTravelRadius) || 0}
              clubs={allKnownClubs}
            />
          </div>
        </div>
      )}
      <button
        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg w-full mt-4 shadow-lg transition-colors duration-150"
        onClick={handleSaveProfile}
        disabled={loading}
      >
        Save Profile
      </button>
    </div>
  );
}

export default ProfilePage;
